import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  LayoutDashboard,
  Scissors,
  Image,
  Star,
  CalendarDays,
  MessageSquare,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Loader2,
  Bell,
  CheckCircle,
  Sparkles,
  Megaphone,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Send,
  Phone,
  Users,
  Mail,
  CalendarPlus,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { API, SITE_URL, authHeaders, resolveUrl, ImageUploadField } from "./shared";
import { TwilioStatusChip } from "./TwilioStatusPanel";
import Ga4StatusPanel from "./Ga4StatusPanel";
import TrafficInsightsPanel from "./TrafficInsightsPanel";

const AnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [wedStats, setWedStats] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics`, authHeaders());
      setData(res.data);
    } catch (err) {
      // error handled silently
    }
    try {
      const wed = await axios.get(`${API}/admin/promo/wednesday/stats`, authHeaders());
      setWedStats(wed.data);
    } catch (err) {
      // error handled silently
    }
  }, []);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      await axios.post(`${API}/admin/digest/send-now`, {}, authHeaders());
      toast.success("Evening digest sent — check your admin email");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send digest");
    } finally {
      setSendingDigest(false);
    }
  };

  if (!data) return <p className="font-sans text-sm text-brand-text-muted">Loading analytics...</p>;

  return (
    <div data-testid="admin-analytics-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-serif text-2xl text-brand-text">Business Dashboard</h2>
          <TwilioStatusChip />
        </div>
        <button
          onClick={sendDigestNow}
          disabled={sendingDigest}
          data-testid="send-digest-now-btn"
          title="Sends a preview of tomorrow's schedule + today's activity to your admin email. Automatically fires every day at 8 PM EST."
          className="flex items-center gap-2 px-3 py-2 border border-brand-border bg-brand-surface text-brand-text font-sans text-xs font-semibold hover:border-brand-gold hover:text-brand-gold transition-colors disabled:opacity-50"
        >
          <Send size={12} className={sendingDigest ? "animate-pulse" : ""} />
          {sendingDigest ? "Sending…" : "Send Tomorrow's Preview"}
        </button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="p-4 border border-brand-border bg-brand-surface" data-testid="metric-daily-revenue">
          <p className="font-sans text-xs text-brand-text-muted uppercase tracking-wider">Today's Revenue</p>
          <p className="font-serif text-2xl text-brand-gold font-semibold mt-1">${data.daily.revenue.toFixed(0)}</p>
          <p className="font-sans text-xs text-brand-text-muted">{data.daily.completed} completed / {data.daily.bookings} total</p>
        </div>
        <div className="p-4 border border-brand-border bg-brand-surface" data-testid="metric-monthly-revenue">
          <p className="font-sans text-xs text-brand-text-muted uppercase tracking-wider">Monthly Revenue</p>
          <p className="font-serif text-2xl text-brand-gold font-semibold mt-1">${data.monthly.revenue.toFixed(0)}</p>
          <p className="font-sans text-xs text-brand-text-muted">{data.monthly.completed} completed / {data.monthly.bookings} total</p>
        </div>
        <div className="p-4 border border-brand-border bg-brand-surface" data-testid="metric-annual-revenue">
          <p className="font-sans text-xs text-brand-text-muted uppercase tracking-wider">Annual Revenue</p>
          <p className="font-serif text-2xl text-brand-gold font-semibold mt-1">${data.annual.revenue.toFixed(0)}</p>
          <p className="font-sans text-xs text-brand-text-muted">{data.annual.bookings} total bookings</p>
        </div>
        <div className="p-4 border border-brand-border bg-brand-surface" data-testid="metric-source">
          <p className="font-sans text-xs text-brand-text-muted uppercase tracking-wider">This Month Source</p>
          <p className="font-sans text-sm text-brand-text mt-2">Online: <span className="text-brand-gold font-semibold">{data.source_split.online}</span></p>
          <p className="font-sans text-sm text-brand-text">Walk-in: <span className="text-brand-gold font-semibold">{data.source_split.walkin}</span></p>
        </div>
      </div>

      {/* Wednesday Silk Press Special performance */}
      {wedStats && (
        <div className="mb-8" data-testid="wednesday-promo-stats">
          <h3 className="font-serif text-lg text-brand-text mb-3">Wednesday Silk Press Special — Performance</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 border border-brand-gold/40 bg-brand-surface" data-testid="wed-stat-banner-clicks">
              <p className="font-sans text-xs text-brand-text-muted uppercase tracking-wider">Banner Clicks</p>
              <p className="font-serif text-2xl text-brand-gold font-semibold mt-1">{wedStats.banner_clicks}</p>
              <p className="font-sans text-xs text-brand-text-muted">{wedStats.banner_views} views</p>
            </div>
            <div className="p-4 border border-brand-gold/40 bg-brand-surface" data-testid="wed-stat-booknow-clicks">
              <p className="font-sans text-xs text-brand-text-muted uppercase tracking-wider">"Book Now" Clicks</p>
              <p className="font-serif text-2xl text-brand-gold font-semibold mt-1">{wedStats.book_now_clicks}</p>
            </div>
            <div className="p-4 border border-brand-gold/40 bg-brand-surface" data-testid="wed-stat-from-banner">
              <p className="font-sans text-xs text-brand-text-muted uppercase tracking-wider">Bookings From Banner</p>
              <p className="font-serif text-2xl text-brand-gold font-semibold mt-1">{wedStats.completed_from_banner}</p>
              <p className="font-sans text-xs text-brand-text-muted">deposit paid</p>
            </div>
            <div className="p-4 border border-brand-gold/40 bg-brand-surface" data-testid="wed-stat-total">
              <p className="font-sans text-xs text-brand-text-muted uppercase tracking-wider">Total Special Bookings</p>
              <p className="font-serif text-2xl text-brand-gold font-semibold mt-1">{wedStats.completed_bookings}</p>
              <p className="font-sans text-xs text-brand-text-muted">all sources</p>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Trend */}
      <div className="mb-8">
        <h3 className="font-serif text-lg text-brand-text mb-4">6-Month Trend</h3>
        <div className="grid grid-cols-6 gap-2">
          {data.monthly_trend.map((m, i) => {
            const maxRev = Math.max(...data.monthly_trend.map((t) => t.revenue), 1);
            const height = Math.max((m.revenue / maxRev) * 120, 4);
            return (
              <div key={m.month} className="text-center" data-testid={`trend-${i}`}>
                <div className="flex flex-col items-center justify-end h-32">
                  <p className="font-sans text-[10px] text-brand-gold mb-1">${m.revenue.toFixed(0)}</p>
                  <div className="w-full bg-brand-gold/80" style={{ height: `${height}px` }} />
                </div>
                <p className="font-sans text-[10px] text-brand-text-muted mt-2">{m.month}</p>
                <p className="font-sans text-[10px] text-brand-text-muted">{m.bookings} bookings</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Services */}
      <div>
        <h3 className="font-serif text-lg text-brand-text mb-4">Top Services (This Month)</h3>
        {data.top_services.length === 0 ? (
          <p className="font-sans text-sm text-brand-text-muted">No bookings this month yet.</p>
        ) : (
          <div className="space-y-2">
            {data.top_services.map((s, i) => (
              <div key={s.name} className="flex items-center gap-4 p-3 border border-brand-border bg-brand-surface" data-testid={`top-service-${i}`}>
                <span className="font-sans text-xs text-brand-gold w-6">{i + 1}.</span>
                <span className="font-sans text-sm text-brand-text flex-1">{s.name}</span>
                <span className="font-sans text-xs text-brand-text-muted">{s.count} bookings</span>
                <span className="font-sans text-sm text-brand-gold font-semibold">${s.revenue.toFixed(0)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <TrafficInsightsPanel />

      <Ga4StatusPanel />
    </div>
  );
};

export default AnalyticsTab;
